import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { FIELD, inr } from "./_shared";

/** Sheet Consumption — Production Design enters L×W×T×qty; backend computes
 *  weight + cost. Editable/deletable any time by production_design + super_admin
 *  (per user choice made in Phase 2 kickoff). */
export default function SheetConsumptionCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts", "admin",
                    "production_design", "production_coordination"].includes(role);
  const canWrite = ["super_admin", "production_design"].includes(role);
  const showMoney = ["super_admin", "accounts", "admin"].includes(role);
  const [state, setState] = useState({ rows: [], total_weight_kg: 0, total_cost: 0, rates: {} });
  const [catalogMaterials, setCatalogMaterials] = useState([]);
  const [form, setForm] = useState({
    material: "", length_mm: "", width_mm: "", thickness_mm: "", quantity: "1", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/costing/project/${p.id}/sheet-consumption`); setState(data); }
    catch { /* noop */ }
    // Materials come from the Quote catalog so the dropdown mirrors
    // Settings → Quote → Materials, regardless of whether a ₹/kg sheet rate
    // has been configured yet on the Costing tab.
    try {
      const { data } = await api.get("/quote/catalog");
      setCatalogMaterials(data?.materials || []);
    } catch { /* fallback below uses sheet-rate keys */ }
  };
  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, []);
  if (!canView) return null;

  // Prefer the Quote catalog list; fall back to sheet-rate keys if the
  // catalog call fails or is empty (older data path).
  const materialOptions = catalogMaterials.length > 0
    ? catalogMaterials.map((m) => m.name).filter(Boolean)
    : Object.keys(state.rates || {});
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/costing/project/${p.id}/sheet-consumption`, {
        material: form.material.trim(),
        length_mm: Number(form.length_mm),
        width_mm: Number(form.width_mm),
        thickness_mm: Number(form.thickness_mm),
        quantity: Number(form.quantity || 1),
        notes: form.notes || null,
      });
      toast.success("Sheet entry added");
      setForm({ material: form.material, length_mm: "", width_mm: "",
                thickness_mm: "", quantity: "1", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this sheet entry?")) return;
    try { await api.delete(`/costing/project/${p.id}/sheet-consumption/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const preview = (() => {
    const l = Number(form.length_mm), w = Number(form.width_mm);
    const t = Number(form.thickness_mm), q = Number(form.quantity || 1);
    if (!l || !w || !t || !q) return null;
    const wt = (l / 1000) * (w / 1000) * t * 8 * q;
    const rate = Number(state.rates?.[form.material.trim()] || 0);
    return { weight: wt, cost: wt * rate, hasRate: !!rate };
  })();

  return (
    <div className="hk-card p-4" data-testid="sheet-consumption-card">
      <div className="hk-tiny-label mb-2">Sheet consumption</div>
      {canWrite && (
        <div className="border border-slate-200 rounded-sm p-2 mb-3 space-y-1">
          <div className="flex gap-1">
            {materialOptions.length > 0 ? (
              <select className={FIELD} value={form.material}
                onChange={(e) => setForm({ ...form, material: e.target.value })}
                data-testid="sc-material">
                <option value="">Material…</option>
                {materialOptions.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            ) : (
              <input className={FIELD} placeholder="Material (e.g. SS 304)"
                value={form.material}
                onChange={(e) => setForm({ ...form, material: e.target.value })}
                data-testid="sc-material" />
            )}
          </div>
          <div className="grid grid-cols-4 gap-1">
            <input className={FIELD} type="number" placeholder="L (mm)"
              value={form.length_mm}
              onChange={(e) => setForm({ ...form, length_mm: e.target.value })}
              data-testid="sc-length" />
            <input className={FIELD} type="number" placeholder="W (mm)"
              value={form.width_mm}
              onChange={(e) => setForm({ ...form, width_mm: e.target.value })}
              data-testid="sc-width" />
            <input className={FIELD} type="number" step="0.1" placeholder="T (mm)"
              value={form.thickness_mm}
              onChange={(e) => setForm({ ...form, thickness_mm: e.target.value })}
              data-testid="sc-thickness" />
            <input className={FIELD} type="number" placeholder="Qty"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              data-testid="sc-quantity" />
          </div>
          <input className={FIELD} placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="sc-notes" />
          {preview && (
            <div className="text-[10px] text-slate-500" data-testid="sc-preview">
              Weight ≈ <b>{preview.weight.toFixed(2)} kg</b>
              {preview.hasRate
                ? <> · Cost ≈ <b>{inr(preview.cost)}</b></>
                : <> · rate not set for material</>}
            </div>
          )}
          <button type="button" onClick={submit}
            disabled={saving || !form.material || !form.length_mm ||
                      !form.width_mm || !form.thickness_mm}
            data-testid="sc-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Add sheet
          </button>
        </div>
      )}
      {state.rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="sc-empty">No entries yet.</div>
      ) : (
        <>
          <ul className="space-y-1.5" data-testid="sc-list">
            {state.rows.map((r) => (
              <li key={r.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">{r.material}</div>
                    <div className="text-slate-600">
                      {r.length_mm}×{r.width_mm}×{r.thickness_mm} mm · Qty {r.quantity}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Weight: <b>{r.weight_kg} kg</b>
                      {showMoney && <> · Cost: <b>{inr(r.cost)}</b> @ {inr(r.rate_per_kg)}/kg</>}
                    </div>
                    {r.notes && <div className="text-slate-500 mt-0.5">{r.notes}</div>}
                  </div>
                  {canWrite && (
                    <button type="button" onClick={() => del(r.id)}
                      data-testid={`sc-del-${r.id}`}
                      className="text-red-600 hover:text-red-800 shrink-0" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs"
            data-testid="sc-totals">
            <span className="font-bold text-slate-700">Total weight: {state.total_weight_kg} kg</span>
            {showMoney && <span className="font-bold text-slate-900">Total: {inr(state.total_cost)}</span>}
          </div>
        </>
      )}

      {/* Send to Production Coordination — appears only once customer has
        approved and Production Design has entered at least one sheet
        consumption row. Owner: production_design + super_admin. */}
      {(() => {
        const pr = p?.production_release || {};
        const canRelease = ["super_admin", "production_design"].includes(role)
          && p?.stage === "design_drawings"
          && pr.status === "pending";
        if (!canRelease) return null;
        const hasSheets = (state?.rows || []).length > 0;
        return (
          <div className="mt-3 pt-3 border-t border-slate-200" data-testid="sc-release-block">
            <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">
              Production release pending
            </div>
            <p className="text-[11px] text-slate-600 mb-2">
              Customer has approved the drawings. Add every sheet used above, then release
              this project to Production Coordination.
            </p>
            <button type="button" disabled={!hasSheets}
              onClick={async () => {
                if (!window.confirm("Release this project to Production Coordination?")) return;
                try {
                  await api.post(`/project/${p.id}/production/release`);
                  toast.success("Released to Production Coordination");
                  window.location.reload();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="w-full text-xs font-black uppercase bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 rounded-sm"
              data-testid="sc-release-btn"
              title={hasSheets ? "Advance to Production Coordination" : "Enter at least one sheet consumption row first"}>
              Send to Production Coordination →
            </button>
            {!hasSheets && (
              <div className="text-[10px] text-slate-500 mt-1 text-center">
                Add at least one sheet consumption row above to unlock the button.
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
